import classNames from 'classnames'
import { Fragment, useState } from 'react'
import { type UserLike } from '@opallabs/concepts'
import { Color, Spacing } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { Avatar, Button, Icon } from '@opallabs/lapidary'
import { InactiveUserTip } from '@opallabs/satellite'
import { CollaboratorGroupMemberList } from './CollaboratorGroupMemberList'
import * as styles from './styles'

type CollaboratorListItemProps<TUserLike> = {
  collaborator: TUserLike
  className?: string
  onRemoveCollaborator: (event: React.MouseEvent, byKeypress: boolean) => void
  permission?: string
  readOnly?: boolean
  removable?: boolean
  showPermissions?: boolean
}

export const CollaboratorListItem = <TUserLike extends UserLike>({
  collaborator,
  className,
  onRemoveCollaborator,
  permission,
  readOnly = false,
  removable = false,
  showPermissions,
}: CollaboratorListItemProps<TUserLike>) => {
  const theme = useTheme()
  const [listExpanded, setListExpanded] = useState(false)

  const isGroup = collaborator.type === 'user_group'
  const memberTotalCount =
    isGroup && 'count' in collaborator && typeof collaborator.count === 'number'
      ? collaborator.members.length < collaborator.count
        ? `${collaborator.members.length}/${collaborator.count}`
        : collaborator.count
      : null
  const avatarAssetUrl = collaborator.asset?.url
  const isActiveWorkspaceMember =
    collaborator.type === 'user'
      ? (collaborator.activeWorkspaceMember ?? true)
      : !collaborator.archived

  return (
    <Fragment>
      <li
        className={classNames('collaborator-list-item', className)}
        css={styles.container}
      >
        <div className="collaborator-user-details" css={styles.details}>
          {!isActiveWorkspaceMember ? (
            <Fragment>
              <InactiveUserTip>
                <Avatar
                  css={[
                    styles.inactiveWorkspaceMember,
                    isGroup ? styles.groupAvatar : undefined,
                  ]}
                  size={Spacing.L}
                  src={avatarAssetUrl}
                  fullName={collaborator.name}
                />
              </InactiveUserTip>
              <InactiveUserTip>
                <span css={styles.inactiveWorkspaceMember}>
                  {collaborator.name}
                </span>
              </InactiveUserTip>
            </Fragment>
          ) : (
            <Fragment>
              <Avatar
                css={isGroup ? styles.groupAvatar : undefined}
                size={Spacing.L}
                src={avatarAssetUrl}
                fullName={collaborator.name}
                initialsLength={isGroup ? 1 : 2}
              />
              <span>{collaborator.name}</span>
            </Fragment>
          )}
          {isGroup && (
            <Fragment>
              <span css={{ color: Color[theme].Text.Gray }}>
                {memberTotalCount ?? collaborator.members.length}
              </span>
              <Button
                variant="ghost"
                name="Expand Collaborator Group"
                icon={
                  <Icon
                    glyph={listExpanded ? 'gem-chevron-n' : 'gem-chevron-s'}
                    ariaHidden
                  />
                }
                disabled={!collaborator.members.length}
                onClick={() => setListExpanded(!listExpanded)}
                styleOverrides={{ button: styles.removeButton }}
              />
            </Fragment>
          )}
        </div>
        <div
          className="collaborator-user-permissions"
          css={styles.permissions(theme)}
        >
          {showPermissions && <span>{permission}</span>}
          {removable && !readOnly && (
            <Button
              variant="ghost"
              name="Remove collaborator"
              aria-label="Remove collaborator"
              icon={
                <Icon glyph="gem-close" width={12} height={12} ariaHidden />
              }
              onClick={onRemoveCollaborator}
              styleOverrides={{ button: styles.removeButton }}
            />
          )}
        </div>
      </li>
      {isGroup && listExpanded && (
        <CollaboratorGroupMemberList members={collaborator.members} />
      )}
    </Fragment>
  )
}
